var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { LocaleController } from '../i18n/LocaleController';
import '../elements/vitesse-icon';
/*
 * BaseSection — the collapsible section primitive, app-wide.
 *
 * A 1:1 Lit port of the agent's BaseSection (header + collapse + actions +
 * loading skeleton + error + CSS-grid height animation), as an INHERITED base
 * class: concrete sections `extends BaseSection` and implement renderContent()
 * (+ optionally sectionHeading()/renderActions()). Light DOM, so it emits the
 * agent's `.model-section` / `.section-*` markup and is styled by the global
 * (now themeable, token-based) SCSS — no per-component styles, light/dark free.
 *
 * Because each subclass is its own custom element, a section is droppable
 * anywhere — the detail page composes them AND a dashboard can mount the same
 * element as a widget. This is the atomic, reusable, inherited shape.
 */
export class BaseSection extends LitElement {
    constructor() {
        super();
        /** Heading shown in the header. Subclasses usually override sectionHeading()
         *  to return an i18n string instead of setting this attribute. */
        this.heading = '';
        this.collapsible = false;
        this.defaultExpanded = true;
        this.loading = false;
        this.error = null;
        /** Chromeless: render ONLY the scoped content — no header, no collapse, no
         *  card chrome — for when a host (a panel-grid tile, a dashboard cell) already
         *  provides the frame + drag handle. The `sectionClass()` wrapper is kept so
         *  the section's scoped SCSS still applies. */
        this.bare = false;
        /** localStorage key to persist the collapse state (optional). */
        this.storageKey = '';
        this.expanded = true;
        this.toggle = () => {
            if (!this.collapsible)
                return;
            this.expanded = !this.expanded;
            if (this.storageKey) {
                try {
                    localStorage.setItem(this.storageKey, String(this.expanded));
                }
                catch {
                    /* private mode / storage disabled — collapse state just won't persist */
                }
            }
        };
        new LocaleController(this); // re-render (re-translate headings/content) on locale change
    }
    createRenderRoot() {
        return this; // light DOM → global token-based SCSS applies
    }
    /** The header title. Defaults to the `heading` attribute; override with t(). */
    sectionHeading() {
        return this.heading;
    }
    /** Section-specific class on the `.model-section` root (e.g.
     *  'model-commands-section') — REQUIRED for the agent's scoped SCSS
     *  (`.model-commands-section .command-buttons` etc.) to apply. */
    sectionClass() {
        return '';
    }
    /** Optional header-right actions (buttons). Return nothing for none. */
    renderActions() {
        return nothing;
    }
    connectedCallback() {
        super.connectedCallback();
        this.expanded = this.initialExpanded();
    }
    initialExpanded() {
        if (!this.collapsible)
            return true;
        if (this.storageKey) {
            try {
                const saved = localStorage.getItem(this.storageKey);
                if (saved !== null)
                    return saved === 'true';
            }
            catch {
                /* private mode / storage disabled — use the default */
            }
        }
        return this.defaultExpanded;
    }
    render() {
        // Chromeless tile/widget body — keep the scoped sectionClass wrapper (so the
        // section's SCSS applies) but drop the header + collapse animation entirely.
        if (this.bare) {
            return html `
        <div class="model-section ${this.sectionClass()} bare ${this.loading ? 'loading' : ''} ${this.error ? 'error' : ''}">
          ${this.error ? html `<div class="section-error">${this.error}</div>` : nothing}
          <div class="section-content">
            ${this.loading
                ? html `<div class="section-loading">
                  <div class="loading-skeleton">
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                    <div class="skeleton-line"></div>
                  </div>
                </div>`
                : this.renderContent()}
          </div>
        </div>
      `;
        }
        const exp = this.expanded;
        const actions = this.renderActions();
        return html `
      <div class="model-section ${this.sectionClass()} ${this.loading ? 'loading' : ''} ${this.error ? 'error' : ''} ${exp ? '' : 'collapsed'}">
        <div
          class="section-header ${this.collapsible ? 'clickable' : ''}"
          @click=${this.collapsible ? this.toggle : undefined}
        >
          <div class="section-title-wrapper"><h3 class="section-title">${this.sectionHeading()}</h3></div>
          <div class="section-header-right">
            ${actions === nothing
            ? nothing
            : html `<div
                  class="section-actions ${exp ? 'visible' : 'hidden'}"
                  @click=${(e) => e.stopPropagation()}
                >
                  ${actions}
                </div>`}
            ${this.collapsible
            ? html `<div class="section-collapse-icon ${exp ? 'expanded' : 'collapsed'}">
                  <vitesse-icon name="chevron-right" size="16"></vitesse-icon>
                </div>`
            : nothing}
          </div>
        </div>
        ${this.error ? html `<div class="section-error">${this.error}</div>` : nothing}
        <div class="section-content-wrapper" style="grid-template-rows:${exp ? '1fr' : '0fr'};opacity:${exp ? '1' : '0'}">
          <div class="section-content-overflow">
            ${this.loading
            ? html `<div class="section-loading">
                  <div class="loading-skeleton">
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                    <div class="skeleton-line"></div>
                  </div>
                </div>`
            : html `<div class="section-content">${this.renderContent()}</div>`}
          </div>
        </div>
      </div>
    `;
    }
}
__decorate([
    property()
], BaseSection.prototype, "heading", void 0);
__decorate([
    property({ type: Boolean })
], BaseSection.prototype, "collapsible", void 0);
__decorate([
    property({ type: Boolean })
], BaseSection.prototype, "defaultExpanded", void 0);
__decorate([
    property({ type: Boolean })
], BaseSection.prototype, "loading", void 0);
__decorate([
    property()
], BaseSection.prototype, "error", void 0);
__decorate([
    property({ type: Boolean })
], BaseSection.prototype, "bare", void 0);
__decorate([
    property()
], BaseSection.prototype, "storageKey", void 0);
__decorate([
    state()
], BaseSection.prototype, "expanded", void 0);
//# sourceMappingURL=base-section.js.map